import { API_DOMAIN, AUTH_URL, ME_URL, USERS_URL, WALLET } from 'config/constants';
import { api } from 'lib/api';
import { checkStatus, getSearch } from 'lib/helpers';

export async function serverStatus(): Promise<'logged' | 'unauthorized' | 'down'> {
	try {
		const response = await api.get<User>(`${API_DOMAIN}${ME_URL}`);
		if (!checkStatus(response)) {
			if (response.status !== 404) {
				return 'unauthorized';
			}
		}
		return 'logged';
	} catch (error) {
		console.warn(error);
	}
	return 'down';
}

export async function fetchVendors() {
	try {
		const response = await api.get(`${API_DOMAIN}${USERS_URL}/vendors`);
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}
		//const data = z.array(zProductSection).parse(response.data);
		return response.data;
	} catch (error) {
		console.error(error);
		return [];
	}
}

export type FetchUserSearchProps = {
	search?: string | null;
	type?: UserType;
};

export type FetchUserProps = FetchUserSearchProps & PaginationProps;

export async function fetchUsers(isAppUsers: boolean, props: FetchUserProps): Promise<PaginateSource<User>> {
	try {
		const sort: Sort = {
			[props.orderBy || 'first_name']: props.order || 'asc',
		};
		const { filter, options } = getSearch(sort, props);
		const response = await api.get(`${API_DOMAIN}${USERS_URL}${isAppUsers ? '/app' : ''}/search`, {
			params: { filter, options },
		});
		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}
		return response.data;
	} catch (error) {
		console.error(error);
		return { elements: [], total: 0 };
	}
}

export async function fetchUser(username: string): Promise<User | null> {
	try {
		const response = await api.get<User | null>(`${API_DOMAIN}${USERS_URL}/${username}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}

type OnBoardingStatusOk = {
	status: 'ok';
};

type OnBoardingStatusPending = {
	status: 'pending';
};

type OnBoardingStatusError = {
	status: 'error';
	tel: string;
};

export type OnBoardingStatus = OnBoardingStatusOk | OnBoardingStatusPending | OnBoardingStatusError;

export async function fetchUserWalletStatus(username: string): Promise<OnBoardingStatus['status'] | null> {
	try {
		const response = await api.get<OnBoardingStatus | null>(`${API_DOMAIN}${USERS_URL}/wallet/${username}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data?.status || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function fetchUserRefId(username: string): Promise<string> {
	try {
		const response = await api.get<{ refId: string } | null>(`${API_DOMAIN}${WALLET}/refId/${username}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data?.refId || '';
	} catch (error) {
		console.error(error);
		return '';
	}
}

export async function fetchUserLastOrder(username: string): Promise<string | null> {
	try {
		const response = await api.get<{ lastOrder: string } | null>(`${API_DOMAIN}${USERS_URL}/last-sale/${username}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data?.lastOrder ?? null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function fetchUserByDocument(document: string): Promise<User | null> {
	try {
		const response = await api.get<User | null>(`${API_DOMAIN}${USERS_URL}/document/${document}`);

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function fetchUserByQRCode(qrData: string): Promise<User | null> {
	try {
		const response = await api.post<{ user: User | null }>(`${API_DOMAIN}wallet/qr/user`, {
			data: qrData,
		});

		if (!checkStatus(response)) {
			throw new Error('invalid credentials');
		}

		return response.data?.user || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export async function updateUser(user: User): Promise<User> {
	if (!user._id) {
		delete user._id;
	}

	const url = user._id ? `${API_DOMAIN}${USERS_URL}/${user.username}` : `${API_DOMAIN}${USERS_URL}`;

	const response = await (user._id ? api.patch<User>(url, user) : api.post<User>(url, { user }));

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data || user;
}

export async function inactivateUser(user: User): Promise<User> {
	const response = await api.patch<User>(`${API_DOMAIN}${USERS_URL}/${user.username}`, {
		...user,
		status: 'inactive',
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data || user;
}

export async function activateUser(user: User): Promise<User> {
	const response = await api.patch<User>(`${API_DOMAIN}${USERS_URL}/${user.username}`, {
		...user,
		status: 'isActive',
	});

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data || user;
}

export async function inviteUsers(users: User | User[]): Promise<true> {
	const url = `${API_DOMAIN}${AUTH_URL}/access/multiple`;

	const response = await api.post(url, Array.isArray(users) ? users : [users]);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return true;
}

export async function deleteUser(user: User): Promise<User> {
	const url = `${API_DOMAIN}${USERS_URL}/${user.username}`;

	const response = await api.delete<User>(url);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data || user;
}

export async function resetPassword(user: User): Promise<User> {
	const url = `${API_DOMAIN}${USERS_URL}/password/${user.username}`;

	const response = await api.delete<User>(url);

	if (!checkStatus(response)) {
		throw new Error('invalid credentials');
	}

	return response.data || user;
}

export async function fetchUserById(id: string): Promise<User | null> {
	try {
		const response = await api.get<User | null | undefined>(`${API_DOMAIN}${USERS_URL}/id/${id}`);

		return response.data || null;
	} catch (error) {
		console.error(error);
		return null;
	}
}

export function isUser(user: any): user is User {
	return !!user && !!user._id;
}
